import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { AutoAwesome, Close, DeleteSweep, Send } from '@mui/icons-material';

const Toolbar = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: 48,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '16px 20px',
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  zIndex: 100,
}));

const CopyComponentsPane = ({
  copyComponents,
  setCopyComponents,
  showCopyResults,
  setCopyResults,
  actionType,
  setActionType,
  canvasLocked,
  loadCopy,
  workflow,
  workflows,
  handleSelectWorkflowType,
  handleSelectWorkflow,
  requestCopy,
  selectedNotes,
  copyDestination,
  copyDestinationLink,
  classes,
}) => {
  const actionOptions = useMemo(() => {
    const opts = [];
    if (selectedNotes && selectedNotes.length > 0) {
      opts.push({ value: 'build_from_selected_notes', label: 'Build workflow from notes', icon: 'ai' });
    }
    opts.push({ value: 'copy_to_current_workflow', label: 'Paste into current workflow' });
    opts.push({ value: 'copy_to_another_workflow', label: 'Paste into another workflow' });
    opts.push({ value: 'delete_selected', label: 'Delete selected', icon: 'delete' });
    return opts;
  }, [selectedNotes]);

  const selectedAction = actionOptions.find(o => o.value === actionType) || null;

  return (
    <>
      {copyComponents && (
        <Fade timeout={600} in={true}>
          <div>
            <Toolbar elevation={0}>
              <Autocomplete
                size="small"
                disableClearable
                openOnFocus
                options={actionOptions}
                getOptionLabel={option => option.label}
                value={selectedAction}
                onChange={(_, newValue) => {
                  if (newValue) handleSelectWorkflowType(newValue.value);
                }}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} style={{ ...props.style, fontSize: 13, padding: '6px 12px' }}>
                    {option.icon === 'ai' && (
                      <AutoAwesome sx={{ color: '#944BA8', mr: 1, fontSize: 16 }} />
                    )}
                    {option.icon === 'delete' && (
                      <DeleteSweep sx={{ color: '#d32f2f', mr: 1, fontSize: 16 }} />
                    )}
                    {option.label}
                  </li>
                )}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      mt: 0.5,
                      '& .MuiAutocomplete-listbox': { py: 0.5 },
                    },
                  },
                }}
                sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } }}
                renderInput={params => (
                  <TextField {...params} variant="outlined" placeholder="Select action..." />
                )}
              />

              {actionType === 'copy_to_another_workflow' && (
                <Autocomplete
                  freeSolo
                  size="small"
                  options={workflows}
                  loading={workflows.length > 0 ? false : true}
                  renderInput={params => (
                    <TextField {...params} variant="outlined" placeholder="Select workflow..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.projectId} style={{ ...props.style, fontSize: 13, padding: '6px 12px' }}>
                      {option.projectName || option.projectId}
                    </li>
                  )}
                  getOptionLabel={option =>
                    option.projectName || option.projectId || ''
                  }
                  onChange={(_, newValue) => {
                    if (newValue) handleSelectWorkflow(newValue);
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        mt: 0.5,
                        '& .MuiAutocomplete-listbox': { py: 0.5 },
                      },
                    },
                  }}
                  sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } }}
                  value={''}
                />
              )}

              <Button
                variant="contained"
                disableElevation
                disabled={
                  canvasLocked ||
                  loadCopy ||
                  !actionType ||
                  (actionType === 'copy_to_another_workflow' && !workflow)
                }
                onClick={requestCopy}
                sx={{
                  minWidth: 40,
                  height: '100%',
                  alignSelf: 'stretch',
                  borderRadius: '8px',
                  backgroundColor: 'text.primary',
                  color: 'background.paper',
                  '&:hover': { backgroundColor: 'text.secondary', color: 'background.paper' },
                  '&.Mui-disabled': { backgroundColor: 'divider', color: 'text.disabled' },
                }}
              >
                {loadCopy ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : (
                  <Send sx={{ fontSize: 16, color: 'inherit' }} />
                )}
              </Button>

              <IconButton
                size="small"
                onClick={() => setCopyComponents(false)}
                sx={{ color: 'text.disabled', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Toolbar>
          </div>
        </Fade>
      )}

      {showCopyResults && (
        <Fade timeout={600} in={true}>
          <div>
            <Toolbar elevation={0} sx={{ gap: 1 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Copied to{' '}
                <Link
                  to={`/project/canvas/${copyDestinationLink}`}
                  reloadDocument={true}
                  style={{ fontWeight: 600, textDecoration: 'underline' }}
                >
                  {copyDestination}
                </Link>
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCopyResults(false)}
                sx={{ color: 'text.disabled', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Toolbar>
          </div>
        </Fade>
      )}
    </>
  );
};

export default CopyComponentsPane;
